import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Table, message, Card, Space, Typography, Button } from 'antd';
import { TagOutlined, SaveOutlined } from '@ant-design/icons';
import { useLLMOverrides } from './useLLMOverrides';
import { predefinedUseCases, planTypes } from './config';
import { getLlmDisplayText, getLlmDisplayComponent } from 'src/utils/llmUtils';

const { Title, Text } = Typography;

const PlanLLMs = ({ selectedOrganization }) => {
  const dispatch = useDispatch();
  const {
    llms,
    llmsLoading,
    llmOverrides,
    llmOverridesLoading,
    initializeTabConfiguration,
    updateTabConfiguration,
    getTabConfiguration,
    getTabSelection,
    setTabSelection,
    getTabUnsavedChanges,
    saveTabConfigurations,
  } = useLLMOverrides();

  // Get configurations, selection, and unsaved changes from Redux
  const configurations = getTabConfiguration('plan');
  const selectedPlanType = getTabSelection('plan');
  const hasUnsavedChanges = getTabUnsavedChanges('plan');

  // Initialize tab configuration when component mounts or when LLM overrides change
  useEffect(() => {
    if (selectedPlanType) {
      initializeTabConfiguration('plan', predefinedUseCases, [
        selectedPlanType,
      ]);
    }
  }, [llmOverrides, llms?.llms, selectedPlanType, initializeTabConfiguration]);

  // Handle configuration updates
  const handleConfigurationChange = (
    useCaseKey,
    settingKey,
    optionKey,
    field,
    value
  ) => {
    updateTabConfiguration(
      'plan',
      useCaseKey,
      settingKey,
      optionKey,
      field,
      value
    );
  };

  // Handle save configurations
  const handleSaveConfigurations = useCallback(async () => {
    if (selectedPlanType) {
      await saveTabConfigurations('plan', predefinedUseCases, [
        selectedPlanType,
      ]);
    }
  }, [saveTabConfigurations, selectedPlanType]);

  // Get LLM options (excluding global model - users can clear to use global)
  const llmOptions =
    llms?.llms?.map((llm, index) => {
      const displayText = getLlmDisplayText(llm);

      return {
        key: llm.llm_id || `llm-${index}`, // Unique key for React
        label: displayText,
        value: llm.model,
        llm_id: llm.llm_id,
        llm: llm, // Store full llm object for rendering
      };
    }) || [];

  // Custom option render to show badges
  const renderOption = (option) => {
    if (!option.llm) return option.label;
    return getLlmDisplayComponent(option.llm);
  };

  // Get plan type options
  const planTypeOptions = planTypes.map((planType) => ({
    label: planType.label,
    value: planType.value,
  }));

  // Create table columns for each use case
  const createTableColumns = () => [
    {
      title: 'Verbosity',
      dataIndex: 'value',
      key: 'value',
      width: '50%',
      render: (text) => <span className="text-gray-800 text-sm">{text}</span>,
    },
    {
      title: 'Model',
      dataIndex: 'llm',
      key: 'llm',
      width: '50%',
      render: (_, record) => (
        <Select
          value={
            configurations[record.useCaseKey]?.[record.settingKey]?.[
              record.optionKey
            ]?.llm || null
          }
          onChange={(value) =>
            handleConfigurationChange(
              record.useCaseKey,
              record.settingKey,
              record.optionKey,
              'llm',
              value
            )
          }
          options={llmOptions}
          loading={llmsLoading}
          className="w-full"
          size="small"
          popupMatchSelectWidth={false}
          showSearch
          allowClear
          placeholder="Select a model"
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          disabled={!selectedPlanType}
          optionRender={renderOption}
        />
      ),
    },
  ];

  // Create table data for a specific use case
  const createTableDataForUseCase = (useCaseKey) => {
    const useCase = predefinedUseCases[useCaseKey];
    const rows = [];

    Object.keys(useCase.settings).forEach((settingKey) => {
      const setting = useCase.settings[settingKey];

      setting.options.forEach((option) => {
        rows.push({
          key: `${useCaseKey}-${settingKey}-${option.toLowerCase()}`,
          useCaseKey,
          settingKey,
          optionKey: option.toLowerCase(),
          value: option,
        });
      });
    });

    return rows;
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center gap-2">
          <Text strong>Select Plan Type:</Text>
          <Select
            placeholder="Select a plan type to configure"
            value={selectedPlanType}
            onChange={(value) => {
              setTabSelection('plan', value);
              if (value) {
                initializeTabConfiguration('plan', predefinedUseCases, [value]);
              }
            }}
            options={planTypeOptions}
            className="w-64"
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      </Card>

      {selectedPlanType ? (
        <div className="space-y-6">
          {Object.keys(predefinedUseCases).map((useCaseKey) => {
            const useCase = predefinedUseCases[useCaseKey];
            return (
              <Card key={useCaseKey}>
                <div className="mb-4">
                  <Title level={4} className="!mb-1">
                    {useCase.title}
                  </Title>
                  <Text type="secondary" className="text-sm">
                    {useCase.description}
                  </Text>
                </div>
                <Table
                  dataSource={createTableDataForUseCase(useCaseKey)}
                  columns={createTableColumns()}
                  rowKey="key"
                  pagination={false}
                  size="middle"
                  tableLayout="fixed"
                  bordered
                  loading={llmsLoading || llmOverridesLoading}
                  locale={{ emptyText: 'No settings available.' }}
                />
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <div className="text-center py-8">
            <Text type="secondary">
              Select a plan type above to configure their LLM preferences
            </Text>
          </div>
        </Card>
      )}

      {/* Save button positioned at bottom right */}
      <div className="flex justify-end pt-4">
        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <span className="text-sm text-orange-600">Unsaved changes</span>
          )}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveConfigurations}
            loading={llmOverridesLoading}
            disabled={!hasUnsavedChanges || !selectedPlanType}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlanLLMs;
